import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { getAllEventTypes } from "../Api/Event";
import Select from "react-select";

import {
  fetchEventByCode,
  fetchParticipantsByEvent,
  fetchRemindersByEvent,
  fetchMoMByEvent,
} from "../Api/ViewEvent";

import {
  updateEvent,
  addParticipants,
  addReminder,
  addMoM,
} from "../Api/Event";

import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { fetchCostCenters } from "../Api/ResourceMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchCustomersByOrgId } from "../Api/InvoiceApi";

import "./EditEvent.css";

const EditEvent = () => {
  const { eventCode } = useParams();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = userDetails?.organisationId;
  const userId = userDetails?.userId;

  const [eventData, setEventData] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  // Dropdown data
  const [companyOptions, setCompanyOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [resourceOptions, setResourceOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const [participants, setParticipants] = useState([]);
  const [existingParticipants, setExistingParticipants] = useState([]);

  const [reminderMinutes, setReminderMinutes] = useState("");
  const [reminderType, setReminderType] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [existingReminders, setExistingReminders] = useState([]);

  const [existingMoM, setExistingMoM] = useState(null);

  // MoM STRUCTURE
  const [momMeta, setMomMeta] = useState({
    preparedBy: "",
    approvedBy: "",
  });

  const [momDetails, setMomDetails] = useState([
    {
      discussionPoint: "",
      decisionTaken: "",
      actionItem: "",
    },
  ]);

  // ================= LOAD DROPDOWNS =================

  const loadCompanyCodes = async () => {
    try {
      const data = await getAllCompanyCodes(orgId);
      const options = data.map((company) => ({
        value: company.companyCode,
        label: `${company.name} (${company.companyCode})`,
      }));
      setCompanyOptions(options);
    } catch (error) {
      console.error("Error fetching company codes:", error);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await getDepartmentsByOrgId(orgId);
      const options = data.map((dept) => ({
        value: dept.guid,
        label: `${dept.deptDescription} (${dept.deptCode})`,
      }));
      setDepartmentOptions(options);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const loadCostCenters = async () => {
    try {
      const data = await fetchCostCenters();
      const options = data.map((center) => ({
        value: center.code,
        label: `${center.description} (${center.code})`,
      }));
      setCostCenterOptions(options);
    } catch (error) {
      console.error("Error fetching cost centers:", error);
    }
  };

  const loadResources = async () => {
    try {
      const data = await getResourcesByOrgId(orgId);
      const options = data.map((resource) => ({
        value: resource.resourceId,
        label: `${resource.name} (${resource.resourceId})`,
      }));
      setResourceOptions(options);
    } catch (error) {
      console.error("Error fetching resources:", error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await fetchCustomersByOrgId(orgId);
      const options = data.map((customer) => ({
        value: customer.customerCode,
        label: `${customer.name} (${customer.customerCode})`,
      }));
      setCustomerOptions(options);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // ================= LOAD MAIN DATA =================

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    loadEvent();
    loadParticipants();
    loadReminders();
    loadMoM();
    loadEventTypes();
    loadCompanyCodes();
    loadDepartments();
    loadCostCenters();
    loadResources();
    loadCustomers();
  };

  const loadEvent = async () => {
    try {
      const data = await fetchEventByCode(orgId, eventCode);

      setEventData({
        eventName: data.eventName,
        eventType: data.eventType,
        eventDate: moment(data.eventDate).format("YYYY-MM-DD"),
        eventEndDate: data.eventEndDate
          ? moment(data.eventEndDate).format("YYYY-MM-DD")
          : "",
        eventStartTime: data.eventStartTime
          ? data.eventStartTime.slice(0, 5)
          : "",
        eventEndTime: data.eventEndTime ? data.eventEndTime.slice(0, 5) : "",
        eventLocation: data.eventLocation,
        organizerName: data.organizerName,
        budgetAllocation: data.budgetAllocation,
        department: data.department,
        eventDescription: data.eventDescription,
        entityType: data.entityType,
        entityId: data.entityId,
        eventCompanyCode: data.eventCompanyCode || "",
        eventCostCenter: data.eventCostCenter || "",
        eventResource: data.eventResource || "",
        eventClient: data.eventClient || "",
      });
    } catch {
      toast.error("Error loading event");
    }
  };

  const loadParticipants = async () => {
    try {
      const data = await fetchParticipantsByEvent(eventCode);
      setExistingParticipants(data);
    } catch {
      toast.error("Error loading participants");
    }
  };

  const loadReminders = async () => {
    try {
      const data = await fetchRemindersByEvent(eventCode);
      setExistingReminders(data);
    } catch {
      toast.error("Error loading reminders");
    }
  };

  const loadMoM = async () => {
    try {
      const data = await fetchMoMByEvent(eventCode);

      setExistingMoM(data);

      setMomMeta({
        preparedBy: data?.preparedBy || "",
        approvedBy: data?.approvedBy || "",
      });

      if (data?.details?.length > 0) {
        setMomDetails(
          data.details.map((d) => ({
            discussionPoint: d.discussionPoint || "",
            decisionTaken: d.decisionTaken || "",
            actionItem: d.actionItem || "",
          })),
        );
      }
    } catch {
      toast.error("Error loading MoM");
    }
  };

  const loadEventTypes = async () => {
    try {
      const data = await getAllEventTypes();
      setEventTypes(data);
      const options = data.map((e) => ({
        value: e.eventTypeGuid,
        label: e.typeName,
      }));
      setEventTypeOptions(options);
    } catch {
      toast.error("Error loading event types");
    }
  };

  // ================= HANDLERS =================

  const handleChange = (e) => {
    setEventData({
      ...eventData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOverviewSave = async () => {
    try {
      const payload = {
        ...eventData,
        eventDate: new Date(eventData.eventDate).toISOString(),
        eventEndDate: eventData.eventEndDate
          ? new Date(eventData.eventEndDate).toISOString()
          : null,
        eventStartTime: eventData.eventStartTime
          ? `${eventData.eventStartTime}:00`
          : null,
        eventEndTime: eventData.eventEndTime
          ? `${eventData.eventEndTime}:00`
          : null,
        eventCompanyCode: eventData.eventCompanyCode || null,
        eventCostCenter: eventData.eventCostCenter || null,
        eventResource: eventData.eventResource || null,
        eventClient: eventData.eventClient || null,
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await updateEvent(eventCode, payload);
      toast.success("Event Updated Successfully");
    } catch {
      toast.error("Failed to update event");
    }
  };

  const handleAddParticipant = async () => {
    try {
      const payload = participants.map((p) => ({
        ...p,
        createdBy: String(userId),
        organisationId: String(orgId),
      }));

      await addParticipants(eventCode, payload);

      toast.success("Participants Added");
      setParticipants([]);
      loadParticipants();
    } catch {
      toast.error("Error adding participants");
    }
  };

  const handleAddReminder = async () => {
    try {
      const payload = {
        reminderMinutes,
        reminderType,
        isSent: isSent === "true",
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addReminder(eventCode, payload);

      toast.success("Reminder Saved");

      setReminderMinutes("");
      setReminderType("");
      setIsSent(false);

      loadReminders();
    } catch {
      toast.error("Error saving reminder");
    }
  };

  const handleSaveMoM = async () => {
    try {
      const payload = {
        preparedBy: momMeta.preparedBy,
        approvedBy: momMeta.approvedBy,
        discussionPoints: momDetails.map((x) => x.discussionPoint),
        decisionsTaken: momDetails.map((x) => x.decisionTaken),
        actionItems: momDetails.map((x) => x.actionItem),
        createdBy: String(userId),
        organisationId: String(orgId),
      };

      await addMoM(eventCode, payload);

      toast.success("MoM Saved");
      loadMoM();
    } catch {
      toast.error("Error saving MoM");
    }
  };

  if (!eventData) return null;

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h2 className="fields-title">
          {eventData.eventName} ({eventCode})
        </h2>

        <Tabs defaultActiveKey="overview">
          {/* ================= OVERVIEW ================= */}

          <Tab eventKey="overview" title="📋 Overview">
            <Card className="mt-3">
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Label>Event Name</Form.Label>
                      <Form.Control
                        name="eventName"
                        value={eventData.eventName}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Event Type</Form.Label>
                      <Select
                        options={eventTypeOptions}
                        value={eventTypeOptions.find(
                          (opt) => opt.value === eventData.eventType,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventType: selected?.value || "",
                          })
                        }
                        placeholder="Select Event Type"
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={4}>
                      <Form.Label>Event Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="eventDate"
                        value={eventData.eventDate}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="eventEndDate"
                        value={eventData.eventEndDate}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Budget</Form.Label>
                      <Form.Control
                        type="number"
                        name="budgetAllocation"
                        value={eventData.budgetAllocation}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={4}>
                      <Form.Label>Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="eventStartTime"
                        value={eventData.eventStartTime}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>End Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="eventEndTime"
                        value={eventData.eventEndTime}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        name="eventLocation"
                        value={eventData.eventLocation}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Label>Organizer</Form.Label>
                      <Form.Control
                        name="organizerName"
                        value={eventData.organizerName}
                        onChange={handleChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Department</Form.Label>
                      <Select
                        options={departmentOptions}
                        value={departmentOptions.find(
                          (opt) => opt.value === eventData.department,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            department: selected?.value || "",
                          })
                        }
                        placeholder="Select Department"
                        isClearable
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={4}>
                      <Form.Label>Company Code</Form.Label>
                      <Select
                        options={companyOptions}
                        value={companyOptions.find(
                          (opt) => opt.value === eventData.eventCompanyCode,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventCompanyCode: selected?.value || "",
                          })
                        }
                        placeholder="Select Company"
                        isClearable
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Cost Center</Form.Label>
                      <Select
                        options={costCenterOptions}
                        value={costCenterOptions.find(
                          (opt) => opt.value === eventData.eventCostCenter,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventCostCenter: selected?.value || "",
                          })
                        }
                        placeholder="Select Cost Center"
                        isClearable
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>Resource</Form.Label>
                      <Select
                        options={resourceOptions}
                        value={resourceOptions.find(
                          (opt) => opt.value === eventData.eventResource,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventResource: selected?.value || "",
                          })
                        }
                        placeholder="Select Resource"
                        isClearable
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Label>Event Client</Form.Label>
                      <Select
                        options={customerOptions}
                        value={customerOptions.find(
                          (opt) => opt.value === eventData.eventClient,
                        )}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            eventClient: selected?.value || "",
                          })
                        }
                        placeholder="Select Client"
                        isClearable
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="eventDescription"
                        value={eventData.eventDescription}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>

                  <Button className="mt-3" onClick={handleOverviewSave}>
                    Update Event
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= PARTICIPANTS ================= */}

          <Tab eventKey="participants" title="👥 Participants">
            <Card className="mt-3">
              <Card.Body>
                {participants.map((p, i) => (
                  <Row key={i} className="mb-2">
                    <Col md={4}>
                      <Form.Control
                        placeholder="User Id"
                        value={p.userId}
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].userId = e.target.value;
                          setParticipants(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Role"
                        value={p.participantRole}
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].participantRole = e.target.value;
                          setParticipants(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Select
                        value={p.status}
                        onChange={(e) => {
                          const arr = [...participants];
                          arr[i].status = e.target.value;
                          setParticipants(arr);
                        }}
                      >
                        <option value="">Status</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Pending">Pending</option>
                        <option value="Declined">Declined</option>
                      </Form.Select>
                    </Col>
                  </Row>
                ))}

                <Button
                  variant="secondary"
                  onClick={() =>
                    setParticipants([
                      ...participants,
                      { userId: "", participantRole: "", status: "" },
                    ])
                  }
                >
                  Add Participant
                </Button>

                <Button className="ms-2" onClick={handleAddParticipant}>
                  Save
                </Button>

                <hr />

                <Table bordered>
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingParticipants.map((p, i) => (
                      <tr key={i}>
                        <td>{p.userId}</td>
                        <td>{p.participantRole}</td>
                        <td>{p.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= REMINDERS ================= */}

          <Tab eventKey="reminders" title="⏰ Reminders">
            <Card className="mt-3">
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Control
                      placeholder="Minutes"
                      value={reminderMinutes}
                      onChange={(e) => setReminderMinutes(e.target.value)}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={reminderType}
                      onChange={(e) => setReminderType(e.target.value)}
                    >
                      <option value="">Type</option>
                      <option value="Email">Email</option>
                      <option value="Notification">Notification</option>
                      <option value="SMS">SMS</option>
                    </Form.Select>
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={isSent}
                      onChange={(e) => setIsSent(e.target.value)}
                    >
                      <option value="">Sent?</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Button className="mt-2" onClick={handleAddReminder}>
                  Save Reminder
                </Button>

                <Table className="mt-3">
                  <thead>
                    <tr>
                      <th>Minutes</th>
                      <th>Type</th>
                      <th>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingReminders.map((r, i) => (
                      <tr key={i}>
                        <td>{r.reminderMinutes}</td>
                        <td>{r.reminderType}</td>
                        <td>{r.isSent ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ================= MOM ================= */}

          <Tab eventKey="mom" title="📝 MoM">
            <Card className="mt-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Control
                      placeholder="Prepared By"
                      value={momMeta.preparedBy}
                      onChange={(e) =>
                        setMomMeta({
                          ...momMeta,
                          preparedBy: e.target.value,
                        })
                      }
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Control
                      placeholder="Approved By"
                      value={momMeta.approvedBy}
                      onChange={(e) =>
                        setMomMeta({
                          ...momMeta,
                          approvedBy: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>

                {momDetails.map((item, i) => (
                  <Row key={i} className="mt-2">
                    <Col md={4}>
                      <Form.Control
                        placeholder="Discussion"
                        value={item.discussionPoint}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].discussionPoint = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Decision"
                        value={item.decisionTaken}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].decisionTaken = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Control
                        placeholder="Action"
                        value={item.actionItem}
                        onChange={(e) => {
                          const arr = [...momDetails];
                          arr[i].actionItem = e.target.value;
                          setMomDetails(arr);
                        }}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  className="mt-2"
                  variant="secondary"
                  onClick={() =>
                    setMomDetails([
                      ...momDetails,
                      {
                        discussionPoint: "",
                        decisionTaken: "",
                        actionItem: "",
                      },
                    ])
                  }
                >
                  Add Row
                </Button>

                <Button className="mt-2 ms-2" onClick={handleSaveMoM}>
                  Save MoM
                </Button>

                <hr />

                <Table bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Discussion</th>
                      <th>Decision</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingMoM?.details?.map((d, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{d.discussionPoint}</td>
                        <td>{d.decisionTaken}</td>
                        <td>{d.actionItem}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
};

export default EditEvent;
